import { createContext, useContext, useState, useEffect } from 'react';
import { EnterpriseProfileApiService } from 'src/api/enterprise-profile/enterprise-profile-api';
import container from 'src/config/inversify-config';
import { Project } from 'src/api/enterprise-profile/types';
import EnterpriseProfileContext from 'src/pages/enterprise/profile-context';

interface ProjectsContextType {
  projects: Project[];
  reload: () => Promise<void>;
  updateProject: (project: Project) => void;
  loading: boolean;
}

const EnterpriseProjectsContext = createContext<ProjectsContextType>({
  projects: [],
  reload: async () => {},
  updateProject: (project: Project) => {},
  loading: false
});

export const useEnterpriseProjects = () => useContext(EnterpriseProjectsContext);

export const EnterpriseProjectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const profileState = useContext(EnterpriseProfileContext);
  const profile = profileState.profile;
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const serviceApi = container.get<EnterpriseProfileApiService>(EnterpriseProfileApiService);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const result = await serviceApi.getEnterpriseProjects(profile.id, { top: 100 });
      setProjects(result?.value || []);
    } catch (e) {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const updateProject = (project: Project) => {
    setProjects((prev) => {
      const idx = prev.findIndex((p) => p.id === project.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = project;
        return next;
      }
      return [...prev, project];
    });
  };

  useEffect(() => {
    if (profile?.id) {
      loadProjects();
    }
    // eslint-disable-next-line
  }, [profile?.id]);

  return (
    <EnterpriseProjectsContext.Provider value={{ projects, reload: loadProjects, updateProject, loading }}>
      {children}
    </EnterpriseProjectsContext.Provider>
  );
};
